import TireInventoryAPI from 'api/tire-inventory.api';
import { BaseHandler } from './base.handler';
import {
  setTireInventory,
  setIsLoading,
  setIsLoaded,
} from 'store/tire-inventory.slice';
import {
  type TireInventoryQueryParams,
  type CreateTireInventoryRequest,
  type InstallTireRequest,
} from 'types/tire-inventory.types';
import {
  type TireTimelineRequest,
  type TireTimelineResponse,
  type TireRecommendationResponse,
} from 'types/tire-timeline.types';
import { updateLoadingState } from 'store/app.slice';
import { showNotification } from 'store/notification.slice';

export default class TireInventoryHandler extends BaseHandler {
  private readonly api: TireInventoryAPI;

  constructor() {
    super();
    this.api = new TireInventoryAPI();
  }

  /**
   * Gets tire inventory with pagination
   */
  async getTireInventory(params: TireInventoryQueryParams = {}): Promise<void> {
    this.dispatch(setIsLoading(true));
    this.dispatch(setIsLoaded(false));

    try {
      const response = await this.api.getTireInventory(params);
      this.dispatch(setTireInventory(response));
      this.dispatch(setIsLoaded(true));
      this.handleSuccess();
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        msg = error.response?.data?.message as string;
      }

      this.handleError(
        'An error occurred while retrieving tire inventory: ' +
          (msg || String(error?.message || 'Unknown error'))
      );
    } finally {
      this.dispatch(setIsLoading(false));
    }
  }

  /**
   * Creates a new tire inventory entry
   */
  async createTireInventory(
    payload: CreateTireInventoryRequest,
    customerId: string
  ): Promise<boolean> {
    this.dispatch(updateLoadingState(true));

    try {
      await this.api.createTireInventory(payload, customerId);
      this.dispatch(updateLoadingState(false));
      this.dispatch(
        showNotification({
          message: 'Tire inventory created successfully.',
          type: 'success',
        })
      );
      return true;
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        // Priority: message field contains detailed validation errors
        msg = error.response?.data?.message as string;

        // Fallback to error field if message is not available
        if (!msg && error.response?.data?.error) {
          msg = error.response?.data?.error as string;
        }
      }

      this.dispatch(updateLoadingState(false));
      this.dispatch(
        showNotification({
          message:
            msg ??
            String(
              error?.message ??
                'An error occurred while creating tire inventory'
            ),
          type: 'error',
        })
      );
      return false;
    }
  }

  /**
   * Installs a tire to an asset
   */
  async installTire(
    payload: InstallTireRequest,
    customerId: string
  ): Promise<boolean> {
    this.dispatch(updateLoadingState(true));

    try {
      await this.api.installTire(payload, customerId);
      this.dispatch(updateLoadingState(false));
      this.dispatch(
        showNotification({
          message: 'Tire installed successfully.',
          type: 'success',
        })
      );
      return true;
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        msg = error.response?.data?.message as string;

        if (!msg && error.response?.data?.error) {
          msg = error.response?.data?.error as string;
        }
      }

      this.dispatch(updateLoadingState(false));
      this.dispatch(
        showNotification({
          message:
            msg ??
            String(
              error?.message ?? 'An error occurred while installing the tire'
            ),
          type: 'error',
        })
      );
      return false;
    }
  }

  /**
   * Gets tire timeline events
   * Returns the data directly (no Redux dispatch, managed locally in the component)
   */
  async getTimeline(
    customerId: string,
    payload: TireTimelineRequest
  ): Promise<TireTimelineResponse | null> {
    try {
      const response = await this.api.getTimeline(customerId, payload);
      return response;
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        msg = error.response?.data?.message as string;
      }

      this.dispatch(
        showNotification({
          message:
            msg ??
            String(
              error?.message ??
                'An error occurred while retrieving tire timeline'
            ),
          type: 'error',
        })
      );
      return null;
    }
  }

  /**
   * Gets AI tire recommendation for an installed tire
   * Returns data directly — state managed locally in the modal component
   */
  async getTireRecommendation(
    customerId: string,
    assetId: string,
    tirePosition: string
  ): Promise<TireRecommendationResponse | null> {
    try {
      const response = await this.api.getTireRecommendation(
        customerId,
        assetId,
        tirePosition
      );
      return response;
    } catch (error: any) {
      let msg = '';
      if (error.isAxiosError) {
        msg = error.response?.data?.message as string;
      }
      this.dispatch(
        showNotification({
          message:
            msg ??
            String(
              error?.message ??
                'An error occurred while fetching AI tire prediction'
            ),
          type: 'error',
        })
      );
      return null;
    }
  }

  /**
   * Exports tire timeline events to PDF
   * Returns the Blob data directly
   */
  async exportTimelinePdf(
    customerId: string,
    payload: TireTimelineRequest
  ): Promise<Blob | null> {
    try {
      const response = await this.api.exportTimelinePdf(customerId, payload);
      return response;
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            msg = json.message || json.error;
          } catch (e) {
            // Unparseable blob error
            msg = 'Failed to download PDF';
          }
        } else {
          msg = error.response?.data?.message as string;
        }
      }

      this.dispatch(
        showNotification({
          message:
            msg ??
            String(
              error?.message ??
                'An error occurred while exporting tire timeline to PDF'
            ),
          type: 'error',
        })
      );
      return null;
    }
  }

  /**
   * Bulk imports tire inventory from Excel/CSV file
   */
  async importTireInventory(file: File, customerId: string): Promise<boolean> {
    this.dispatch(updateLoadingState(true));

    try {
      const response = await this.api.importTireInventory(file, customerId);
      this.dispatch(updateLoadingState(false));

      const { successCount, failedCount, errors } = response || {};

      if (failedCount > 0) {
        const errorDetails =
          errors && errors.length > 0
            ? `: ${errors
                .slice(0, 3)
                .map((e: any) => `Row ${e.row}: ${e.error}`)
                .join(', ')}${errors.length > 3 ? '...' : ''}`
            : '';
        this.dispatch(
          showNotification({
            message: `Imported with warnings. Success: ${
              successCount || 0
            }. Failed: ${failedCount || 0}${errorDetails}`,
            type: 'warning',
          })
        );
      } else {
        this.dispatch(
          showNotification({
            message: `Tire inventory spreadsheet imported successfully. Imported ${
              successCount || 0
            } tires.`,
            type: 'success',
          })
        );
      }
      return true;
    } catch (error: any) {
      let msg: string = '';

      if (error.isAxiosError) {
        msg = error.response?.data?.message as string;
      }

      this.dispatch(updateLoadingState(false));
      this.dispatch(
        showNotification({
          message:
            msg ??
            String(
              error?.message ??
                'An error occurred while importing tire inventory spreadsheet'
            ),
          type: 'error',
        })
      );
      return false;
    }
  }
}
